import type { ChangeEvent } from 'react';
import type { Match } from '@/api/types';
import { Card } from '@/components/ui';
import { formatDateTime } from '@/lib/datetime';
import styles from './ergebnistipp.module.css';

/** Eingabewert pro Spiel; leerer String = noch nicht getippt. */
export interface PredictionDraft {
  home: string;
  away: string;
}

interface MatchRowProps {
  match: Match;
  draft: PredictionDraft;
  /** Punkte des eigenen Tipps, falls bereits ausgewertet. */
  points: number | null;
  locked: boolean;
  /** DFB-Pokal: getippt wird das Ergebnis nach Elfmeterschießen (Hinweis). */
  isPokal: boolean;
  onChange: (matchId: number, side: 'home' | 'away', value: string) => void;
}

/** Erlaubt nur Ziffern 0–99 als Tippeingabe. */
function sanitize(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 2);
  return digits;
}

export function MatchRow({ match, draft, points, locked, isPokal, onChange }: MatchRowProps) {
  const handle = (side: 'home' | 'away') => (e: ChangeEvent<HTMLInputElement>) =>
    onChange(match.id, side, sanitize(e.target.value));

  const finished =
    match.status === 'finished' && match.home_goals !== null && match.away_goals !== null;

  return (
    <Card>
      <div className={styles.match}>
        <div className={styles.matchKickoff}>{formatDateTime(match.kickoff_at)}</div>

        <div className={`${styles.team} ${styles.teamHome}`}>
          <span className={styles.teamName}>{match.home_team.name}</span>
          {match.home_team.logo_url && (
            <img className={styles.logo} src={match.home_team.logo_url} alt="" />
          )}
        </div>

        <div className={styles.scoreInputs}>
          <input
            className={styles.scoreInput}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={`Tore ${match.home_team.name}`}
            value={draft.home}
            disabled={locked}
            onChange={handle('home')}
          />
          <span className={styles.colon}>:</span>
          <input
            className={styles.scoreInput}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={`Tore ${match.away_team.name}`}
            value={draft.away}
            disabled={locked}
            onChange={handle('away')}
          />
        </div>

        <div className={`${styles.team} ${styles.teamAway}`}>
          {match.away_team.logo_url && (
            <img className={styles.logo} src={match.away_team.logo_url} alt="" />
          )}
          <span className={styles.teamName}>{match.away_team.name}</span>
        </div>
      </div>

      {isPokal && (
        <div className={styles.pokalNote}>
          Getippt wird das Ergebnis nach Elfmeterschießen.
        </div>
      )}

      {(finished || points !== null) && (
        <div className={styles.result}>
          {finished && (
            <span>
              Endstand: {match.home_goals}:{match.away_goals}
            </span>
          )}
          {points !== null && <span className={styles.points}>{points} Pkt.</span>}
        </div>
      )}
    </Card>
  );
}
